import { Component } from '@theme/component';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const ROTATION_INTERVAL = 3000;

/**
 * @typedef {object} StoriesRefs
 * @property {HTMLElement[]} scenes
 * @property {HTMLElement[]} media
 * @property {HTMLElement[]} panels
 * @property {HTMLButtonElement[]} hotspots
 * @property {HTMLButtonElement[]} [closeButtons]
 * @property {HTMLButtonElement[]} selectors
 * @property {HTMLElement[]} progress
 * @property {HTMLElement} controls
 * @property {HTMLElement} status
 */

/** @extends {Component<StoriesRefs>} */
export class MervauxShoppableStories extends Component {
  requiredRefs = ['controls', 'status'];
  #controller;
  #observer;
  #motion;
  #timer;
  #animation;
  #cardAnimation;
  #progressAnimation;
  #remaining = ROTATION_INTERVAL;
  #startedAt = 0;
  #index = 0;
  #openIndex = -1;
  #visible = false;
  #wheelTotal = 0;
  #wheelTime = 0;
  #lastWheelChange = -Infinity;
  #pointer;
  #pointerActivation;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    this.#cleanup();
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const scenes = this.refs.scenes || [];
    this.dataset.enhanced = 'true';
    this.#index = Math.min(this.#index, Math.max(0, scenes.length - 1));
    this.#openIndex = -1;
    this.refs.controls.hidden = scenes.length < 2;
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    for (const hotspot of this.refs.hotspots || []) hotspot.hidden = hotspot.disabled;
    for (const button of this.refs.closeButtons || []) button.hidden = false;
    this.#render(false);
    this.#placeControls();

    this.#motion.addEventListener('change', () => {
      this.#resetProgress();
      if (this.#motion.matches) { this.#animation?.cancel(); this.#cardAnimation?.cancel(); }
      this.#syncPlayback();
    }, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#observe, { signal });
    this.addEventListener('focusin', this.#syncPlayback, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#syncPlayback(true);
    }, { signal });
    this.addEventListener('keydown', this.#onKey, { signal });
    this.addEventListener('pointerdown', (event) => {
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      this.#pointerActivation = button ? { button, at: performance.now() } : undefined;
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#pointer = undefined;
      this.#syncPlayback();
    }, { signal });
    this.addEventListener('shopify:block:select', (event) => {
      const index = scenes.findIndex((scene) => scene.dataset.blockId === event.detail?.blockId);
      if (index >= 0) { this.#go(index, false); this.#openProduct(index); }
    }, { signal });
    for (const media of this.refs.media || []) {
      media.addEventListener('wheel', this.#onWheel, { passive: false, signal });
      media.addEventListener('pointerdown', this.#onPointerDown, { signal });
      media.addEventListener('pointerup', this.#onPointerUp, { signal });
      media.addEventListener('pointercancel', () => { this.#pointer = undefined; this.#syncPlayback(); }, { signal });
    }
    this.#observe();
  }

  #cleanup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#resetProgress();
    this.#animation?.cancel();
    this.#cardAnimation?.cancel();
    this.#visible = false;
    this.#wheelTotal = 0;
    this.#wheelTime = 0;
    this.#lastWheelChange = -Infinity;
    this.#pointer = undefined;
    this.#pointerActivation = undefined;
  }

  #observe = () => {
    this.#observer?.disconnect();
    this.#visible = false;
    this.#syncPlayback();
    const media = this.refs.media?.[this.#index];
    if (!media) return;
    this.#observer = new IntersectionObserver(([entry]) => {
      if (this.#controller.signal.aborted) return;
      this.#visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= .45);
      this.#syncPlayback();
    }, { root: getIntersectionRoot(), threshold: [0, .45] });
    // Observe the photo, since the reserved mobile card space can exceed the viewport.
    this.#observer.observe(media);
  };

  #stopClock() {
    if (this.#timer !== undefined) {
      this.#remaining = Math.max(0, this.#remaining - (performance.now() - this.#startedAt));
      clearTimeout(this.#timer);
      this.#timer = undefined;
    }
    this.#progressAnimation?.pause();
    this.dataset.playing = 'false';
  }

  #resetProgress() {
    this.#stopClock();
    this.#remaining = ROTATION_INTERVAL;
    this.#progressAnimation?.cancel();
    this.#progressAnimation = undefined;
  }

  // The thumbnail overlay and timer share the same paused interval.
  #syncPlayback = (focusLeaving = false) => {
    const focused = focusLeaving === true ? false : this.contains(document.activeElement);
    const enabled = this.dataset.autoplay === 'true' && this.dataset.designMode !== 'true' && !this.#motion?.matches;
    if (!enabled || focused || this.#pointer || this.#openIndex >= 0 || !this.#visible || document.hidden || (this.refs.scenes?.length || 0) < 2) {
      this.#stopClock();
      return;
    }
    if (this.#timer !== undefined) return;
    if (!this.#progressAnimation) {
      this.#progressAnimation = this.refs.progress?.[this.#index]?.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: ROTATION_INTERVAL, easing: 'linear', fill: 'forwards' }
      );
    } else this.#progressAnimation.play();
    this.dataset.playing = 'true';
    this.#startedAt = performance.now();
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      if (this.isConnected && !this.#controller.signal.aborted) this.#go((this.#index + 1) % this.refs.scenes.length, false);
    }, this.#remaining);
  };

  #render(announce) {
    (this.refs.scenes || []).forEach((scene, index) => {
      scene.hidden = index !== this.#index;
      scene.inert = index !== this.#index;
    });
    (this.refs.selectors || []).forEach((selector, index) => {
      if (index === this.#index) selector.setAttribute('aria-current', 'true');
      else selector.removeAttribute('aria-current');
    });
    this.#renderProducts();
    if (announce) this.refs.status.textContent = this.refs.scenes[this.#index]?.dataset.announcement || '';
    this.#syncPlayback();
  }

  #renderProducts() {
    (this.refs.panels || []).forEach((panel, index) => {
      const open = index === this.#openIndex && index === this.#index;
      panel.dataset.open = String(open);
      panel.inert = !open;
      panel.setAttribute('aria-hidden', String(!open));
      this.refs.hotspots?.[index]?.setAttribute('aria-expanded', String(open));
    });
  }

  #placeControls(focusedControl) {
    const sceneGroup = this.refs.scenes?.[0]?.parentElement;
    if (!sceneGroup || sceneGroup.nextElementSibling === this.refs.controls) return;
    // The footer follows both the photo and product panel in reading and tab order.
    const focused = focusedControl || (this.refs.controls.contains(document.activeElement) ? document.activeElement : null);
    sceneGroup.after(this.refs.controls);
    focused?.focus({ preventScroll: true });
  }

  #go(index, announce = true) {
    if (index === this.#index || !this.refs.scenes?.[index]) return;
    const oldScene = this.refs.scenes[this.#index];
    let focusedControl = this.refs.controls.contains(document.activeElement) ? document.activeElement : null;
    // Move focus before making the replaced scene inert.
    if (oldScene?.contains(document.activeElement)) {
      focusedControl = this.refs.selectors?.[index];
      focusedControl?.focus({ preventScroll: true });
    }
    this.#resetProgress();
    this.#animation?.cancel();
    this.#cardAnimation?.cancel();
    this.#openIndex = -1;
    this.#index = index;
    this.#visible = false;
    this.#render(announce);
    this.#placeControls(focusedControl);
    if (!this.#motion.matches) {
      this.#animation = this.refs.media[index].animate(
        [{ opacity: .12, transform: 'translateY(18px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 680, easing: 'cubic-bezier(.22,.61,.36,1)' }
      );
    }
    this.#observe();
  }

  previousScene() { this.#go((this.#index - 1 + this.refs.scenes.length) % this.refs.scenes.length); }
  nextScene() { this.#go((this.#index + 1) % this.refs.scenes.length); }

  /** @param {MouseEvent} event */
  selectScene(event) {
    const button = this.#eventButton(event);
    if (!button) return;
    this.#go(Number(button.dataset.index));
    if (this.#wasPointerClick(event, button)) button.blur();
    this.#resetProgress();
    this.#syncPlayback();
  }

  /** @param {MouseEvent} event */
  toggleProduct(event) {
    const button = this.#eventButton(event);
    if (!button) return;
    const index = Number(button.dataset.index);
    if (index !== this.#index) return;
    if (this.#openIndex === index) this.#closeProduct(event, button);
    else this.#openProduct(index, true);
  }

  #openProduct(index, reveal = false) {
    if (this.refs.hotspots?.[index]?.disabled) return;
    this.#openIndex = index;
    this.#renderProducts();
    this.#syncPlayback();
    this.#cardAnimation?.cancel();
    if (!this.#motion.matches) {
      this.#cardAnimation = this.refs.panels[index].animate(
        [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 420, easing: 'cubic-bezier(.22,.61,.36,1)' }
      );
    }
    if (reveal) this.#revealOnMobile(this.refs.panels[index]);
  }

  #revealOnMobile(element) {
    if (element && matchMedia('(max-width: 989px)').matches) {
      const bounds = element.getBoundingClientRect();
      const viewport = getIntersectionRoot()?.getBoundingClientRect() || { top: 0, bottom: window.innerHeight };
      if (bounds.top < viewport.top || bounds.bottom > viewport.bottom) {
        element.scrollIntoView({ block: 'nearest', behavior: this.#motion.matches ? 'instant' : 'smooth' });
      }
    }
  }

  /** @param {MouseEvent} event */
  closeProduct(event) {
    const button = this.#eventButton(event);
    if (button) this.#closeProduct(event, button);
  }

  #closeProduct(event, button) {
    if (this.#openIndex < 0) return;
    const index = this.#openIndex;
    const pointer = this.#wasPointerClick(event, button);
    if (pointer) {
      button.blur();
      if (this.refs.panels[index].contains(document.activeElement)) document.activeElement?.blur();
    }
    else this.refs.hotspots?.[index]?.focus({ preventScroll: true });
    this.#cardAnimation?.cancel();
    this.#openIndex = -1;
    this.#renderProducts();
    if (!pointer) this.#revealOnMobile(this.refs.hotspots[index]);
    this.#syncPlayback();
  }

  #eventButton(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-index]') : null;
    return button && this.contains(button) ? button : null;
  }

  #wasPointerClick(event, button) {
    const activation = this.#pointerActivation;
    this.#pointerActivation = undefined;
    return event.detail > 0 && activation?.button === button && performance.now() - activation.at < 1500;
  }

  /** @param {KeyboardEvent} event */
  #onKey = (event) => {
    if (event.key === 'Escape' && this.#openIndex >= 0) {
      event.preventDefault();
      this.#closeProduct(event, this.refs.hotspots[this.#openIndex]);
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target?.closest('input, textarea, select, [contenteditable="true"]')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const rtl = getComputedStyle(this).direction === 'rtl';
    if ((event.key === 'ArrowRight') !== rtl) this.nextScene();
    else this.previousScene();
  };

  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    if (this.#openIndex >= 0 || event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY) || event.target?.closest('a, button, input, textarea, select')) return;
    const now = performance.now();
    const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.refs.media[this.#index].clientHeight : 1);
    const direction = Math.sign(delta);
    const next = this.#index + direction;
    if (next < 0 || next >= this.refs.scenes.length || now - this.#lastWheelChange < 850) {
      this.#wheelTotal = 0;
      return;
    }
    if (now - this.#wheelTime > 180 || Math.sign(this.#wheelTotal) !== direction) this.#wheelTotal = 0;
    this.#wheelTime = now;
    this.#wheelTotal += delta;
    if (Math.abs(this.#wheelTotal) < 70) return;
    if (event.cancelable) event.preventDefault();
    this.#lastWheelChange = now;
    this.#wheelTotal = 0;
    this.#go(next);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (this.#openIndex >= 0 || event.pointerType !== 'touch' || event.target?.closest('a, button')) return;
    this.#pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
    this.#syncPlayback();
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const start = this.#pointer;
    this.#pointer = undefined;
    this.#syncPlayback();
    if (!start || start.id !== event.pointerId || this.#openIndex >= 0) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const next = this.#index + ((dx < 0) !== rtl ? 1 : -1);
    if (next >= 0 && next < this.refs.scenes.length) this.#go(next);
  };
}

if (!customElements.get('mervaux-shoppable-stories')) customElements.define('mervaux-shoppable-stories', MervauxShoppableStories);

